// Bridge handshake on app boot.
//
// The Liquid mount script renders an HMAC-signed payload into
// window.__MD_STUDIO_CONFIG__.bridge proving the visitor is a
// Shopify-NCA-signed-in customer. We post that to /auth/bridge to mint
// the md_studio_session cookie. From that point on every API call
// rides on the cookie.
//
// If ?staff_token= is in the URL, the SPA was opened by a Studio Admin
// in "view as staff" mode. We swap the token for a staff cookie BEFORE
// the regular bridge — staff cookie wins.

import { bridgeAuth, fetchMe, staffBridge, type StaffActingAs } from './api';
import type { CustomerSummary } from './types';

export type AuthState =
  | { status: 'loading' }
  | {
      status: 'authenticated';
      customer: CustomerSummary;
      staffActingAs: StaffActingAs | null;
    }
  | { status: 'unauthenticated' };

/**
 * Boot order:
 *   1. If ?staff_token= is in the URL, swap it for a staff cookie via
 *      /staff-bridge. Strip the query param after success so it doesn't
 *      sit in browser history. Staff cookie wins over the regular one.
 *   2. Otherwise, if a Liquid bridge payload is present, run regular auth.
 *   3. Probe /me to confirm a session and surface staff metadata.
 */
export async function bootstrapAuth(): Promise<AuthState> {
  // Staff impersonation hand-off — runs first because the staff cookie
  // takes precedence over a regular customer session if both are present.
  const url = new URL(window.location.href);
  const staffToken = url.searchParams.get('staff_token');
  let staffMode = false;
  if (staffToken) {
    try {
      await staffBridge(staffToken);
      staffMode = true;
    } catch {
      // Token rejected (expired, bad sig). Fall through — /me will tell us
      // whether we have any usable session.
    }
    url.searchParams.delete('staff_token');
    window.history.replaceState({}, '', url.toString());
  }

  const bridge = window.__MD_STUDIO_CONFIG__?.bridge;
  if (bridge && !staffMode) {
    try {
      await bridgeAuth(bridge);
    } catch {
      // Fall through — bridge can fail for benign reasons (clock skew, secret rotation).
    }
  }

  try {
    const me = await fetchMe();
    return {
      status: 'authenticated',
      customer: me.customer,
      staffActingAs: me.staff_acting_as,
    };
  } catch {
    return { status: 'unauthenticated' };
  }
}
